import {
  ExportFormat,
  getDownloadLinkSoda3,
  getDownloadLink,
  getExportFormats,
  IGNORED_FORMATS_FOR_DATE,
  supportsGeoExport,
} from 'common/downloadLinks';
import { lastInChain } from 'common/explore_grid/lib/selectors';
import { FeatureFlags } from 'common/feature_flags';
import { ClientContextVariable } from 'common/types/clientContextVariable';
import { isCompilationSucceeded } from 'common/types/compiler';
import { TransitionSuccessType } from 'common/explore_grid/lib/feature-flag-helpers';
import {
  SoQLType,
  TypedExpr,
  TypedSoQLColumnRef,
  isLeaf,
  isTypedColumnRef,
  isTypedFunCall,
  soqlRendering,
} from 'common/types/soql';
import { ResourceState, ResourceType } from './ExportModalTypes';
import { PossibleVersions } from './VersionRadioButtons';
import { Option } from 'ts-option';
import { AssetType, View } from 'common/types/view';
import { fetchTranslation } from 'common/locale';
import { toTypedOverrideParamString, toV3OverrideParams } from 'common/core/client_context_variables';
import { chain, Dictionary, isUndefined, omitBy } from 'lodash';
import moment from 'moment';
import { selectionWithProvenanceFromQueryAnalysis } from 'common/explore_grid/lib/soql-helpers';

const t = (k: string) => fetchTranslation(k, 'shared.explore_grid.export_dataset_modal');

export const getResourceState = (
  view: View,
  query: Option<TransitionSuccessType>,
  clientContextVariables: ClientContextVariable[],
  queryString: Option<string>,
  isTableViz: boolean,
  apiVersion: PossibleVersions,
  fourfour?: string,
  revisionSeq?: number,
  currentResourceState?: ResourceState,
  useResourceName?: boolean,
  odataSelectClause?: string,
) => {
  // if there's a query, do this...
  const formats = query
    .map((qs) => {
      const selectionTypes = qs.fold(
        (q) =>
          q.map((compiled) =>
            lastInChain(compiled.analyzed).selection.reduce((acc: SoQLType[], selectedExpr) => {
              if (selectedExpr.expr.soql_type !== null) {
                return [selectedExpr.expr.soql_type, ...acc];
              }
              return acc;
            }, []),
          ).get,
        (q) =>
          q.map((analyzed) =>
            analyzed.outputSchema.reduce((acc: SoQLType[], os) => {
              if (os.type) {
                return [os.type, ...acc];
              }
              return acc;
            }, []),
          ).get,
      );

      // :@ is invalid in XML. If the select includes a computed column, core will
      // mark it as hidden on xml export, causing query-coordinator to throw a
      // column-not-found error if we try to select it. This happens even if the
      // computed column is aliased.. So if a computed column is in the select,
      // do not show XML export as an option.
      function invalidSelectForXML(q: TransitionSuccessType): boolean {
        // don't care about other compilation states since export is disabled for them
        if (isCompilationSucceeded(q.get.get)) {
          if (q.isLeft) {
            const ast = q.left.get.analyzed;
            if (isLeaf(ast)) {
              // have to recursively crawl ast because user could pass select computed column
              // to a function
              return hasComputedColumn(ast?.value?.selection.map((select) => select.expr));
            }
          } else {
            const ast = q.right.get.ast;
            if (isLeaf(ast)) {
              // have to recursively crawl ast because user could pass select computed column
              // to a function
              const selectionItems = selectionWithProvenanceFromQueryAnalysis(q.right.get);
              return hasComputedColumn(selectionItems.map((si) => si.expr));
            }
          }
        }
        return false;
      }

      return getExportFormats(view, isTableViz, invalidSelectForXML(qs), selectionTypes);
    })
    .getOrElseValue(getExportFormats(view, isTableViz));

  let maybeQueryString;
  // We either use the queryString or we use the query.
  if (queryString.isDefined) {
    // We are using the select and where clauses
    maybeQueryString = queryString;
  } else {
    // We are using the query
    maybeQueryString = query.map((qs) =>
      qs.fold(
        (compiled) => compiled.map((c) => soqlRendering.unwrap(c.rendering)).get,
        (analyzed) => analyzed.map((a) => a.formattedText).get,
      ),
    );
  }

  const downloadResources = makeResourcesDownloads(
    view,
    maybeQueryString,
    formats,
    clientContextVariables,
    fourfour,
    revisionSeq,
  );

  const apiResources = makeResourcesApi(
    view,
    maybeQueryString,
    apiVersion,
    clientContextVariables,
    useResourceName,
  );

  const OdataResources = makeResourcesOdata(view, odataSelectClause);

  const getSelectedDownloadResource = () => {
    if (currentResourceState) {
      // see if the selected resource is in our list, otherwise fall through and use the default resource
      const foundResource = downloadResources.find(
        (v) => v.value === currentResourceState.selectedDownloadResource?.value,
      );
      if (foundResource) return foundResource;
    }
    return downloadResources.find((v) => v.defaultType);
  };

  const getSelectedApiResource = () => {
    if (currentResourceState) {
      const foundResource = apiResources.find(
        (v) => v.value === currentResourceState.selectedApiResource.value,
      );
      if (foundResource) return foundResource;
    }
    return apiResources.find((v) => v.defaultType);
  };

  const getSelectedOdataResource = () => {
    if (currentResourceState) {
      const foundResource = OdataResources.find(
        (v) => v.value === currentResourceState.selectedOdataResource.value,
      );
      if (foundResource) return foundResource;
    }
    return OdataResources.find((v) => v.defaultType);
  };

  const resourceState: ResourceState = {
    selectedDownloadResource: getSelectedDownloadResource(),
    apiResources: apiResources,
    odataResources: OdataResources,
    downloadResources: downloadResources,
    // there will always be a defaultType because we hard coded it
    // @ts-expect-error TS(2322) FIXME: Type 'ResourceType | undefined' is not assignable ... Remove this comment to see the full error message
    selectedApiResource: getSelectedApiResource(),
    // there will always be a defaultType because we hard coded it
    // @ts-expect-error TS(2322) FIXME: Type 'ResourceType | undefined' is not assignable ... Remove this comment to see the full error message
    selectedOdataResource: getSelectedOdataResource(),
  };

  return resourceState;
};

const protocolAndHost = () => `${window.location.protocol}//${window.location.host}`;

const hasComputedColumn = (exprs: TypedExpr[]): boolean => {
  return exprs.some((expr) => hasComputedColumnHelper(expr));
};

const hasComputedColumnHelper = (expr: TypedExpr): boolean => {
  if (isTypedFunCall(expr)) {
    return hasComputedColumn(expr.args);
  } else if (isTypedColumnRef(expr)) {
    // tsc doesn't realize we just have a TypedSoQLColumnRef here
    return (expr as TypedSoQLColumnRef).value.startsWith(':@');
  } else {
    return false;
  }
};

const makeResourcesApi = (
  view: View,
  query: Option<string>,
  apiVersion: PossibleVersions,
  clientContextVariables: ClientContextVariable[],
  useResourceName?: boolean,
): ResourceType[] => {
  // I believe we would want to set this based if the user is using a resource_name
  // We optionally use resource name or four-four
  const identifier = useResourceName && view?.resourceName ? view.resourceName : view.id;

  const resourceLinks = [
    {
      label: t('csv'),
      value: 'csv' as ExportFormat,
      url: queryUrl(identifier, query, 'csv', clientContextVariables, apiVersion === PossibleVersions.Soda3),
      defaultType: false,
    },
    {
      label: t('json'),
      value: 'json' as ExportFormat,
      url: queryUrl(identifier, query, 'json', clientContextVariables, apiVersion === PossibleVersions.Soda3),
      defaultType: true,
    },
  ];

  if (supportsGeoExport(view)) {
    resourceLinks.push({
      label: t('geojson'),
      value: 'geojson' as ExportFormat,
      url: queryUrl(
        identifier,
        query,
        'geojson',
        clientContextVariables,
        apiVersion === PossibleVersions.Soda3,
      ),
      defaultType: false,
    });
  }
  return resourceLinks;
};

const downloadOptions = (
  view: View,
  format: ExportFormat,
  queryString: Option<string>,
  fourfour?: string,
  revisionSeq?: number,
) => {
  let queryParams: Dictionary<any> = omitBy(
    {
      query: queryString.nonEmpty ? queryString.get : undefined,
      fourfour: fourfour,
      revisionSeq: revisionSeq,
    },
    isUndefined,
  );

  if (queryString.nonEmpty) {
    queryParams = {
      ...queryParams,
      read_from_nbe: true,
      version: 2.1,
    };
  }

  // this parameter ensures the browser doesn't use a cached version after an update
  if (view) {
    queryParams.cacheBust = chain([view.rowsUpdatedAt, view.createdAt, view.viewLastModified])
      .compact()
      .max()
      .value();
  }

  if (IGNORED_FORMATS_FOR_DATE.indexOf(format) === -1) {
    queryParams.date = moment().format('YYYYMMDD');
  }

  return {
    queryParams,
    cname: window.location.hostname,
    format,
  };
};

const downloadOptionsSoda3 = (view: View, format: ExportFormat, revisionSeq?: number) => {
  const queryParams: Dictionary<any> = omitBy(
    {
      revisionSeq: revisionSeq,
    },
    isUndefined,
  );

  // this parameter ensures the browser doesn't use a cached version after an update
  if (view) {
    queryParams.cacheBust = chain([view.rowsUpdatedAt, view.createdAt, view.viewLastModified])
      .compact()
      .max()
      .value();
  }

  if (format === 'shapefile') {
    queryParams.date = moment().format('YYYYMMDD');
  }

  // When the view is a derived view and we are working with a revision of it, add additional parameter
  if (isDerivedViewRevision(view, revisionSeq)) {
    queryParams.asIfView = view.id;
  }

  return {
    queryParams,
    cname: window.location.hostname,
    format,
  };
};

const makeResourcesDownloads = (
  view: View,
  queryString: Option<string>,
  exportFormats: ExportFormat[],
  clientContextVariables: ClientContextVariable[],
  fourfour?: string,
  revisionSeq?: number,
): ResourceType[] => {
  return exportFormats.map((format) => {
    let url;
    if (FeatureFlags.value('soda3_exports')) {
      const { id, modifyingViewUid } = view;
      // When the view is a derived view and we are working with a revision of it, we need to query the parent view.
      const viewId = isDerivedViewRevision(view, revisionSeq) && modifyingViewUid ? modifyingViewUid : id;
      url = getDownloadLinkSoda3(viewId, downloadOptionsSoda3(view, format, revisionSeq)) || '';
    } else {
      url = getDownloadLink(view.id, downloadOptions(view, format, queryString, fourfour, revisionSeq)) || '';
      // add any client context vars
      if (clientContextVariables.length) {
        url = `${url}&${toTypedOverrideParamString(clientContextVariables)}`;
      }
    }
    return {
      label: t(format),
      value: format,
      url: url,
      defaultType: format === 'csv',
      // soda3 use posts so we want to set a body here
      requestBody: makeRequestBody(queryString, format, clientContextVariables),
    };
  });
};

// used by soda3
const makeRequestBody = (
  queryString: Option<string>,
  format: ExportFormat,
  clientContextVariables: ClientContextVariable[],
) => {
  if (FeatureFlags.value('soda3_exports')) {
    // we only want to add the values to the body if they are provided.
    return {
      ...(queryString.nonEmpty && { query: queryString.get }),
      ...(clientContextVariables.length && {
        parameters: { qualified: toV3OverrideParams(clientContextVariables) },
      }),
      ...(format === 'csv_europe' && { serializationOptions: { separator: ';' } }),
    };
  } else {
    return null;
  }
};

const makeResourcesOdata = (view: View, odataSelectClause = ''): ResourceType[] => {
  const url = (V2orV4ApiPath: string) =>
    `${protocolAndHost()}/${V2orV4ApiPath}/${view.id}${odataSelectClause}`;
  const resourceLinks = [
    {
      label: 'OData V4',
      value: 'odataUrlV4' as ExportFormat,
      url: url('api/odata/v4'),
      defaultType: true,
    },
    {
      label: 'OData V2',
      value: 'odataUrlV2' as ExportFormat,
      url: url('Odata.svc'),
      defaultType: false,
    },
  ];

  return resourceLinks;
};

const queryUrl = (
  ff: string,
  query: Option<string>,
  type: 'csv' | 'json' | 'geojson',
  clientContextVariables: ClientContextVariable[],
  useV3SodaApiEndpoints: boolean = FeatureFlags.value('soda3_exports'),
) => {
  const shouldIncludeClientContextVariables = clientContextVariables.length && !useV3SodaApiEndpoints;
  let url = useV3SodaApiEndpoints
    ? `${protocolAndHost()}/api/v3/views/${ff}/query.${type}` // v3
    : `${protocolAndHost()}/resource/${ff}.${type}`; // v2
  if (query.nonEmpty || shouldIncludeClientContextVariables) {
    url = `${url}?`;
  }
  if (query.nonEmpty) {
    url = `${url}${!useV3SodaApiEndpoints ? '$' : ''}query=${encodeURIComponent(query.get)}`;
  }
  if (shouldIncludeClientContextVariables) {
    url = `${url}&${toTypedOverrideParamString(clientContextVariables)}`;
  }
  return url;
};

function isDerivedView(view: View): boolean {
  const { assetType, modifyingViewUid } = view;
  return assetType === AssetType.Filter && !!modifyingViewUid;
}

function isDerivedViewRevision(view: View, revisionSeq?: number): boolean {
  return isDerivedView(view) && revisionSeq !== undefined;
}
